import ExcelJS from "exceljs";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../../../../backend/firebaseConfig";

const ADVISERS_PATH = ["Authorized Members", "Advisers", "Data"];

const asStatusList = (status) => {
  if (Array.isArray(status)) return status.filter(Boolean).map(String);
  if (status === null || status === undefined || status === "") return [];
  return [String(status)];
};

export const getAuthorisedProductColumns = (status) => {
  const statuses = asStatusList(status).map((value) => value.trim().toUpperCase());
  const columns = [];
  if (statuses.includes("FP")) columns.push("Financial Planners");
  if (statuses.includes("MA")) columns.push("Mortgage");
  columns.push("Protection");
  return columns;
};

const getNames = (base, personal) => {
  const firstName = String(
    personal?.forename ||
      personal?.firstName ||
      base?.firstName ||
      base?.forename ||
      "",
  ).trim();
  const lastName = String(
    personal?.surname ||
      personal?.lastName ||
      base?.lastName ||
      base?.surname ||
      "",
  ).trim();
  return { firstName, lastName };
};

const fetchAdviserRow = async (snapshot) => {
  const adviserKey = snapshot.id;
  const base = snapshot.data() || {};
  const personalRef = doc(db, ...ADVISERS_PATH, adviserKey, "Personal Details", "Data");
  const ioDetailsRef = doc(db, ...ADVISERS_PATH, adviserKey, "IO Details", "Data");
  const [personalSnapshot, ioSnapshot] = await Promise.all([
    getDoc(personalRef),
    getDoc(ioDetailsRef),
  ]);
  const personal = personalSnapshot.exists() ? personalSnapshot.data() : {};
  const ioDetails = ioSnapshot.exists() ? ioSnapshot.data() : {};
  const status = asStatusList(base.status);
  const { firstName, lastName } = getNames(base, personal);
  const email = String(base.email || adviserKey).trim();

  return {
    adviserKey,
    email,
    adviserCRMContactID: Array.isArray(ioDetails.ioAdviserId)
      ? ioDetails.ioAdviserId[0] ?? ""
      : ioDetails.ioAdviserId ?? "",
    firstName,
    lastName,
    adviserName: [firstName, lastName].filter(Boolean).join(" ") || adviserKey,
    status,
    authorisedProductColumns: getAuthorisedProductColumns(status),
    authorisedProductTypes: getAuthorisedProductColumns(status),
  };
};

export const fetchAdviserGatingRows = async () => {
  const adviserSnapshot = await getDocs(collection(db, ...ADVISERS_PATH));
  const rows = await Promise.all(adviserSnapshot.docs.map(fetchAdviserRow));

  return rows.sort((left, right) =>
    left.adviserName.localeCompare(right.adviserName, "en-GB", {
      sensitivity: "base",
      numeric: true,
    }),
  );
};

const EXPECTED_HEADERS = [
  "AdviserCRMContactID*",
  "First Name",
  "Last Name",
  "Authorised Product Types*",
];

export const readAdviserGatingTemplate = async (file) => {
  if (!file) throw new Error("Please upload the adviser gating Excel file first.");
  if (!/\.xlsx$/i.test(file.name || "")) {
    throw new Error("The adviser gating template must be an .xlsx file.");
  }

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sourceSheet = workbook.getWorksheet("UpdateAdviserGating") || workbook.worksheets[0];
  if (!sourceSheet) throw new Error("The workbook does not contain an adviser gating sheet.");

  const headers = EXPECTED_HEADERS.map((_, index) =>
    String(sourceSheet.getCell(1, index + 1).value || "").trim(),
  );
  if (headers.some((header, index) => header !== EXPECTED_HEADERS[index])) {
    throw new Error(`The first sheet must contain: ${EXPECTED_HEADERS.join(", ")}.`);
  }

  const referenceSheet = workbook.getWorksheet("Sheet1") || workbook.worksheets[1];
  const referenceRows = [];
  if (referenceSheet) {
    referenceSheet.eachRow({ includeEmpty: false }, (row) => {
      referenceRows.push([1, 2, 3].map((column) => row.getCell(column).value ?? ""));
    });
  }

  const permissionColumns = {};
  ["Protection", "Mortgage", "Financial Planners"].forEach((heading, columnIndex) => {
    permissionColumns[heading] = referenceRows
      .slice(1)
      .map((row) => String(row[columnIndex] ?? "").trim())
      .filter(Boolean);
  });

  return { referenceRows, permissionColumns };
};

export const applyTemplatePermissions = (rows, template) =>
  rows.map((row) => {
    const seen = new Set();
    const authorisedProductTypes = row.authorisedProductColumns.flatMap((column) =>
      (template?.permissionColumns?.[column] || []).filter((permission) => {
        const key = permission.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }),
    );
    return { ...row, authorisedProductTypes };
  });

export const explodeAdviserGatingRows = (rows) =>
  rows.flatMap((row) =>
    row.authorisedProductTypes.map((authorisedProductType, productIndex) => ({
      ...row,
      authorisedProductType,
      productIndex,
    })),
  );

export const createAdviserGatingWorkbook = async (rows, template) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Rosemount Staff Portal";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("UpdateAdviserGating", {
    views: [{ state: "frozen", ySplit: 1 }],
  });
  sheet.columns = [
    { header: "AdviserCRMContactID*", key: "adviserCRMContactID", width: 25 },
    { header: "First Name", key: "firstName", width: 24 },
    { header: "Last Name", key: "lastName", width: 28 },
    {
      header: "Authorised Product Types*",
      key: "authorisedProductTypes",
      width: 42,
    },
  ];

  rows.forEach((row) => {
    sheet.addRow({
      adviserCRMContactID: row.adviserCRMContactID,
      firstName: row.firstName,
      lastName: row.lastName,
      authorisedProductTypes:
        row.authorisedProductType || row.authorisedProductTypes?.[0] || "",
    });
  });

  sheet.autoFilter = `A1:D${Math.max(rows.length + 1, 1)}`;
  sheet.getRow(1).height = 30;
  sheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  sheet.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF192747" },
  };
  sheet.getRow(1).alignment = {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  };
  sheet.getColumn("adviserCRMContactID").numFmt = "0";
  sheet.getColumn("authorisedProductTypes").alignment = {
    vertical: "middle",
    wrapText: true,
  };

  if (template?.referenceRows?.length) {
    const referenceSheet = workbook.addWorksheet("Sheet1", {
      views: [{ state: "frozen", ySplit: 1 }],
    });
    referenceSheet.addRows(template.referenceRows);
    referenceSheet.columns = [
      { width: 48 },
      { width: 52 },
      { width: 52 },
    ];
    referenceSheet.getRow(1).font = { bold: true };
    referenceSheet.autoFilter = `A1:C${template.referenceRows.length}`;
  }

  return workbook.xlsx.writeBuffer();
};
